import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { postRequest } from "../../Requests/postRequests";
import { login } from "../../slices/staffSlice";
import { show } from '../../slices/toastSlice';
import Logo from "../../assets/images/logo.png";

// Sign-in is two steps for every staff member:
//   1. email + password
//   2. a code from their authenticator app (or a one-time backup code).
// The first time, step 2 is setup: scan a QR code, confirm a code, save backup codes.
type Step = "password" | "code" | "setup" | "backup";

const Spinner = () => (
    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
);

const buttonClass = "w-full h-11 flex items-center justify-center rounded-xl bg-light-blue hover:bg-dark-blue text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed mt-2";

function Login() {

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [step, setStep] = useState<Step>("password");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [code, setCode] = useState<string>("");
    const [useBackup, setUseBackup] = useState<boolean>(false);
    const [mfaToken, setMfaToken] = useState<string>("");
    const [setup, setSetup] = useState<any>(null);
    const [session, setSession] = useState<any>(null);
    const [savedCodes, setSavedCodes] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [process, setProcess] = useState<boolean>(false);

    const toast = useCallback((type: string, message: string) => {
        dispatch(show({ type, state: true, message }));
    }, [dispatch]);

    const finish = useCallback((res: any) => {
        setSuccess(true);
        // the login slice is persisted in a cookie: never pass backup codes into it
        const { backupCodes, backupCodesLeft, ...sessionData } = res || {};
        dispatch(login(sessionData));
        navigate("/");
    }, [dispatch, navigate]);

    // the 10-minute sign-in session ran out: start again
    const restart = useCallback((message?: string) => {
        if (message) toast("error", message);
        setStep("password");
        setCode("");
        setMfaToken("");
        setSetup(null);
    }, [toast]);

    const handlePassword = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setProcess(true);

        try {
            const res = await postRequest(`/auth/staff/login`, { email, password });

            if (res?.mfaRequired) {
                setMfaToken(res.mfaToken);
                setCode("");
                setUseBackup(false);
                setStep("code");
            } else if (res?.mfaSetupRequired) {
                const s = await postRequest(`/auth/staff/mfa/setup`, { mfaToken: res.mfaToken });
                if (s?.err || !s?.qrDataUrl) {
                    toast("error", s?.err || "Could not start two-factor setup");
                } else {
                    setMfaToken(res.mfaToken);
                    setSetup(s);
                    setCode("");
                    setStep("setup");
                }
            } else {
                toast("error", res?.err || "Login failed");
            }
        } catch (error) {
            toast("error", "Network error or server unavailable");
        }
        setProcess(false);
    }, [email, password, toast]);

    const handleCode = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setProcess(true);
        try {
            const res = await postRequest(`/auth/staff/mfa/verify`, { mfaToken, code: code.trim() });
            if (res?.accessToken) {
                if (typeof res.backupCodesLeft === "number") {
                    toast(res.backupCodesLeft <= 2 ? "error" : "success",
                        `Backup code used. You have ${res.backupCodesLeft} left. Ask an administrator to reset your 2FA if you are running out.`);
                }
                finish(res);
            } else if (/expired/i.test(res?.err || "")) {
                restart(res.err);
            } else {
                toast("error", res?.err || "That code is not right");
            }
        } catch (error) {
            toast("error", "Network error or server unavailable");
        }
        setProcess(false);
    }, [mfaToken, code, toast, finish, restart]);

    const handleEnable = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setProcess(true);
        try {
            const res = await postRequest(`/auth/staff/mfa/enable`, { mfaToken, code: code.trim() });
            if (res?.accessToken && Array.isArray(res.backupCodes)) {
                setSession(res);
                setStep("backup");
            } else if (/expired|start the setup again/i.test(res?.err || "")) {
                restart(res.err);
            } else {
                toast("error", res?.err || "That code is not right");
            }
        } catch (error) {
            toast("error", "Network error or server unavailable");
        }
        setProcess(false);
    }, [mfaToken, code, toast, restart]);

    const downloadCodes = () => {
        const text = `CedisPay staff portal backup codes for ${email}\nEach code works once.\n\n${(session?.backupCodes || []).join("\n")}\n`;
        const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = "cedispay-backup-codes.txt";
        a.click();
        URL.revokeObjectURL(url);
    };

    const copyCodes = async () => {
        try {
            await navigator.clipboard.writeText((session?.backupCodes || []).join("\n"));
            toast("success", "Backup codes copied");
        } catch (error) {
            toast("error", "Could not copy. Download them instead.");
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-gray-950 flex items-center justify-center p-4">
            <div className="w-full max-w-sm">

                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <img src={Logo} alt="CedisPay" className="h-10 w-auto object-contain" />
                </div>

                {/* Card */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8">

                    {step === "password" && (
                        <>
                            <div className="mb-7">
                                <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                    Sign in
                                </h1>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Enter your credentials to access the portal
                                </p>
                            </div>

                            <form onSubmit={handlePassword} className="space-y-5">
                                <div>
                                    <label htmlFor="email" className="form-label">Email address</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        autoComplete="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="form-input"
                                        placeholder="you@cedispay.com"
                                    />
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label htmlFor="password" className="form-label !mb-0">Password</label>
                                        <Link
                                            to="/forgot-password"
                                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                        >
                                            Forgot password?
                                        </Link>
                                    </div>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="form-input"
                                        placeholder="••••••••"
                                    />
                                </div>

                                <button type="submit" disabled={process} className={buttonClass}>
                                    {process ? <Spinner /> : "Continue"}
                                </button>
                            </form>
                        </>
                    )}

                    {step === "code" && (
                        <>
                            <div className="mb-7">
                                <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                    {useBackup ? "Enter a backup code" : "Enter your code"}
                                </h1>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    {useBackup
                                        ? "Use one of the backup codes you saved when you set up two-factor sign-in. Each works once."
                                        : "Open your authenticator app and enter the 6-digit code for CedisPay Staff."}
                                </p>
                            </div>

                            <form onSubmit={handleCode} className="space-y-5">
                                <div>
                                    <label htmlFor="code" className="form-label">{useBackup ? "Backup code" : "Authentication code"}</label>
                                    <input
                                        type="text"
                                        id="code"
                                        name="code"
                                        autoComplete="one-time-code"
                                        inputMode={useBackup ? "text" : "numeric"}
                                        autoFocus
                                        required
                                        maxLength={useBackup ? 11 : 6}
                                        value={code}
                                        onChange={(e) => setCode(useBackup ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ""))}
                                        className="form-input font-mono tracking-widest text-center text-lg"
                                        placeholder={useBackup ? "XXXXX-XXXXX" : "123456"}
                                    />
                                </div>

                                <button type="submit" disabled={process} className={buttonClass}>
                                    {process ? <Spinner /> : success ? <CheckCircleIcon className="w-5 h-5" /> : "Sign in"}
                                </button>
                            </form>

                            <div className="mt-5 flex items-center justify-between text-xs">
                                <button type="button" onClick={() => { setUseBackup(!useBackup); setCode(""); }} className="text-blue-600 dark:text-blue-400 hover:underline">
                                    {useBackup ? "Use authenticator app instead" : "Lost your phone? Use a backup code"}
                                </button>
                                <button type="button" onClick={() => restart()} className="text-gray-500 hover:underline">
                                    Back
                                </button>
                            </div>
                        </>
                    )}

                    {step === "setup" && setup && (
                        <>
                            <div className="mb-5">
                                <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                    Set up two-factor sign-in
                                </h1>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Required for all staff. You only do this once.
                                </p>
                            </div>

                            <ol className="space-y-4 text-sm text-gray-700 dark:text-gray-300">
                                <li>
                                    <span className="font-semibold">1.</span> Install an authenticator app on your phone, such as Google Authenticator, Microsoft Authenticator or Authy.
                                </li>
                                <li>
                                    <span className="font-semibold">2.</span> In the app, add an account and scan this code:
                                    <div className="mt-3 flex justify-center">
                                        <img src={setup.qrDataUrl} alt="QR code for your authenticator app" className="w-44 h-44 rounded-lg border border-gray-200" />
                                    </div>
                                    <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">Can't scan? Enter this key instead:</p>
                                    <p className="mt-1 font-mono text-xs break-all rounded-md bg-gray-50 dark:bg-gray-800 px-2 py-1.5 select-all">
                                        {String(setup.secret).replace(/(.{4})/g, "$1 ").trim()}
                                    </p>
                                </li>
                                <li>
                                    <span className="font-semibold">3.</span> Enter the 6-digit code the app shows:
                                </li>
                            </ol>

                            <form onSubmit={handleEnable} className="mt-3 space-y-4">
                                <input
                                    type="text"
                                    id="setup-code"
                                    name="code"
                                    autoComplete="one-time-code"
                                    inputMode="numeric"
                                    required
                                    maxLength={6}
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                    className="form-input font-mono tracking-widest text-center text-lg"
                                    placeholder="123456"
                                />
                                <button type="submit" disabled={process} className={buttonClass}>
                                    {process ? <Spinner /> : "Turn on two-factor sign-in"}
                                </button>
                            </form>
                        </>
                    )}

                    {step === "backup" && session && (
                        <>
                            <div className="mb-5">
                                <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                    Save your backup codes
                                </h1>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    If you lose your phone, each of these codes lets you sign in once. Keep them somewhere safe, not on the same phone. You won't see them again.
                                </p>
                            </div>

                            <div className="grid grid-cols-2 gap-2 rounded-lg bg-gray-50 dark:bg-gray-800 p-3 font-mono text-sm text-gray-800 dark:text-gray-100">
                                {session.backupCodes.map((c: string) => <span key={c} className="text-center">{c}</span>)}
                            </div>

                            <div className="mt-3 flex gap-2">
                                <button type="button" onClick={downloadCodes} className="flex-1 h-9 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
                                    Download
                                </button>
                                <button type="button" onClick={copyCodes} className="flex-1 h-9 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
                                    Copy
                                </button>
                            </div>

                            <label className="mt-5 flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                                <input type="checkbox" checked={savedCodes} onChange={(e) => setSavedCodes(e.target.checked)} className="mt-0.5" />
                                I have saved my backup codes
                            </label>

                            <button type="button" disabled={!savedCodes} onClick={() => finish(session)} className={buttonClass}>
                                Continue to the portal
                            </button>
                        </>
                    )}
                </div>

                {/* Footer */}
                {step === "password" && (
                    <p className="mt-5 text-center text-sm text-gray-500 dark:text-gray-400">
                        New staff? Ask an administrator to send you an invite.
                    </p>
                )}

            </div>
        </div>
    );
}

export default Login;
